import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function RegistryProjectDetail() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/registry/projects/${id}`);
      setProject(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load project');
    }
  };

  useEffect(() => { load(); }, [id]);

  const submitProject = async () => {
    try {
      await api.post(`/registry/projects/${id}/submit`);
      toast.success('Project submitted for review');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit project');
    }
  };

  const verifyProject = async (decision) => {
    try {
      await api.post(`/registry/projects/${id}/verification`, { decision, notes: `Admin ${decision}` });
      toast.success(`Project ${decision}`);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification action failed');
    }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('document_type', 'evidence');
      await api.post(`/registry/projects/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!project) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="page-shell page-stack">
      <section className="surface-panel glow-surface" style={{ padding: '1.6rem' }}>
        <div className="section-tag">{project.registry_status}</div>
        <h1 className="section-title">{project.project_name}</h1>
        <p className="section-copy">{project.description}</p>
        <div className="hero-note" style={{ marginTop: '1rem' }}>
          <div className="info-chip">{project.credit_type}</div>
          <div className="info-chip">{project.methodology_code}</div>
          <div className="info-chip">{project.vintage}</div>
          <div className="info-chip">Verification: {project.verification_status}</div>
        </div>
      </section>

      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag">Documents</div>
          <div style={{ marginTop: '1rem', display: 'grid', gap: '0.75rem' }}>
            {(project.documents || []).map((doc) => (
              <a key={doc.id} href={doc.url} target="_blank" rel="noreferrer" className="surface-panel" style={{ padding: '0.9rem', textDecoration: 'none' }}>
                <div className="stat-label">{doc.document_type}</div>
                <div className="stat-sub">{doc.file_name}</div>
              </a>
            ))}
            {user && (
              <label className="btn btn-secondary" style={{ justifyContent: 'center', cursor: 'pointer' }}>
                {uploading ? 'Uploading...' : 'Upload evidence'}
                <input type="file" hidden onChange={uploadDocument} />
              </label>
            )}
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag">Workflow</div>
          <div className="panel-stack" style={{ marginTop: '1rem' }}>
            <div className="copy-sm">Tokenization eligibility: {project.tokenization_eligibility}</div>
            {user?.id === project.owner_user_id && project.registry_status === 'draft' && (
              <button className="btn btn-primary" onClick={submitProject}>Submit for review</button>
            )}
            {isAdmin && (
              <div className="inline-actions">
                <button className="btn btn-primary" onClick={() => verifyProject('approved')}>Approve</button>
                <button className="btn btn-danger" onClick={() => verifyProject('rejected')}>Reject</button>
              </div>
            )}
            <Link to="/admin/registry" className="btn btn-secondary">Open admin registry</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
